'use client'

import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/extended-button'
import { Input } from '@/components/ui/input'
import { Copy, Image as ImageIcon, Loader2, MessageCircle } from 'lucide-react'
import {
  FaFacebookF,
  FaInstagram,
  FaXTwitter,
  FaLinkedinIn,
  FaPinterestP,
} from 'react-icons/fa6'
import { SiGmail, SiMicrosoftoutlook } from 'react-icons/si'
import Link from 'next/link'
import { toast } from 'react-toastify'
import { buildShareUrl } from '@/lib/sharing'
import { useLogShareMutation } from 'graphql/generated/graphql'

interface RecipeShareModalProps {
  open: boolean
  onClose: () => void
  recipe: {
    slug: string
    recipe_id?: number | null
    recipe_name?: string | null
    resourcePath?: string | null
    recipe_img_url?: string | null
    recipe_image?: { resource_path?: string | null } | null
  }
  userSlug?: string | null
}

type ChannelKey =
  | 'instagram'
  | 'facebook'
  | 'gmail'
  | 'outlook'
  | 'pinterest'
  | 'twitter'
  | 'linkedin'
  | 'sms'

// Public broadcast channels — require a recipe photo to look good.
// Pinterest uses the recipe OG card, so it is not photo-gated.
// Personal channels (Gmail, Outlook) are always allowed.
const PHOTO_GATED: ChannelKey[] = ['instagram', 'facebook', 'twitter', 'linkedin']

export function RecipeShareModal({ open, onClose, recipe, userSlug }: RecipeShareModalProps) {
  const [igBusy, setIgBusy] = useState(false)
  const [isTouchDevice, setIsTouchDevice] = useState(false)
  const [igDownloadModalOpen, setIgDownloadModalOpen] = useState(false)
  const [downloadingSize, setDownloadingSize] = useState<'square' | 'story' | null>(null)
  const [igCachedBlob, setIgCachedBlob] = useState<Blob | null>(null)
  const [igStoryCachedBlob, setIgStoryCachedBlob] = useState<Blob | null>(null)
  const [igPreviewUrl, setIgPreviewUrl] = useState<string | null>(null)
  const [igPanelOpen, setIgPanelOpen] = useState(false)
  const [logShare] = useLogShareMutation()

  useEffect(() => {
    if (!igCachedBlob) { setIgPreviewUrl(null); return }
    const url = URL.createObjectURL(igCachedBlob)
    setIgPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [igCachedBlob])

  useEffect(() => {
    if (!open) setIgPanelOpen(false)
  }, [open])

  useEffect(() => {
    // `pointer: coarse` matches phones + tablets; desktops with a mouse don't match.
    // The sms: URI scheme is only useful on those touch devices, so we render the
    // Messages button conditionally.
    setIsTouchDevice(window.matchMedia('(pointer: coarse)').matches)
  }, [])

  const hasPhoto = !!(
    recipe.resourcePath ||
    recipe.recipe_img_url ||
    recipe.recipe_image?.resource_path
  )

  const recipePath = `/recipes/${recipe.slug}`
  const contentId = String(recipe.recipe_id || recipe.slug)
  const shareUrl =
    typeof window !== 'undefined'
      ? buildShareUrl(recipePath, 'recipe', contentId, userSlug || undefined)
      : ''
  const recipeName = recipe.recipe_name || 'this recipe'
  const refQs = userSlug ? `&ref=${encodeURIComponent(userSlug)}` : ''

  // Pre-warm both square and story OG images on mobile when the modal opens.
  // navigator.share({files}) on iOS requires the blob to be ready BEFORE the
  // tap — any async fetch between the gesture and the share call breaks the
  // user-gesture chain and iOS refuses to open the share sheet.
  useEffect(() => {
    if (!open || !isTouchDevice) return
    setIgCachedBlob(null)
    setIgStoryCachedBlob(null)
    fetch(`/api/og/recipe/${recipe.slug}?size=square${refQs}`)
      .then((res) => (res.ok ? res.blob() : null))
      .then((blob) => { if (blob) setIgCachedBlob(blob) })
      .catch(() => {})
    fetch(`/api/og/recipe/${recipe.slug}?size=story${refQs}`)
      .then((res) => (res.ok ? res.blob() : null))
      .then((blob) => { if (blob) setIgStoryCachedBlob(blob) })
      .catch(() => {})
  }, [open, isTouchDevice])

  const logShareEvent = (channel: string) => {
    if (!userSlug) return
    logShare({
      variables: {
        input: {
          content_type: 'recipe',
          content_id: contentId,
          share_channel: channel,
          ref_slug: userSlug,
        },
      },
    }).catch(() => {})
  }

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      toast.success('Link copied')
      logShareEvent('clipboard')
    } catch {
      toast.error('Failed to copy')
    }
  }

  const shareToInstagram = async (size: 'square' | 'story' = 'square') => {
    if (!isTouchDevice) {
      setIgDownloadModalOpen(true)
      return
    }

    setIgBusy(true)
    try {
      let blob = (size === 'square' ? igCachedBlob : igStoryCachedBlob)
      if (!blob) {
        const ogUrl = `/api/og/recipe/${recipe.slug}?size=${size}${refQs}`
        let res = await fetch(ogUrl)
        if (!res.ok) res = await fetch(ogUrl)
        if (!res.ok) throw new Error('Image generation failed')
        blob = await res.blob()
      }

      // Story on iOS: write blob to clipboard then open instagram-stories:// directly,
      // bypassing the system share sheet. visibilitychange tells us if Instagram opened.
      if (size === 'story' && /iPad|iPhone|iPod/.test(navigator.userAgent)) {
        try {
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
          window.location.href = 'instagram-stories://share'
          const opened = await new Promise<boolean>((resolve) => {
            const t = setTimeout(() => resolve(false), 1500)
            const handler = () => {
              if (document.visibilityState === 'hidden') {
                clearTimeout(t)
                document.removeEventListener('visibilitychange', handler)
                resolve(true)
              }
            }
            document.addEventListener('visibilitychange', handler)
          })
          if (opened) {
            logShareEvent('instagram_story_direct')
            onClose()
            return
          }
          // Instagram not installed — fall through to navigator.share below
        } catch {
          // clipboard write not supported — fall through to navigator.share
        }
      }

      const file = new File([blob], `${recipe.slug}.png`, { type: 'image/png' })
      const canWebShare =
        typeof navigator.canShare === 'function' &&
        navigator.canShare({ files: [file] })

      if (canWebShare) {
        await navigator.share({
          files: [file],
          title: recipeName,
          text: `Check out this recipe: ${recipeName}`,
          url: shareUrl,
        })
        logShareEvent('instagram_web_share')
        onClose()
      } else {
        setIgDownloadModalOpen(true)
      }
    } catch (err) {
      // AbortError fires when the user dismisses the share sheet — not a real failure.
      if ((err as DOMException)?.name !== 'AbortError') {
        console.error('IG share failed:', err)
        toast.error('Failed to prepare image')
      }
    } finally {
      setIgBusy(false)
    }
  }

  const downloadIgImage = async (size: 'square' | 'story') => {
    setDownloadingSize(size)
    try {
      const url = `/api/og/recipe/${recipe.slug}?size=${size}${refQs}`
      let res = await fetch(url)
      if (!res.ok) res = await fetch(url)
      if (!res.ok) throw new Error('Image generation failed')
      const blob = await res.blob()
      const objectUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = `${recipe.slug}-${size}.png`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(objectUrl)
      logShareEvent(`instagram_download_${size}`)
    } catch {
      toast.error('Failed to download image')
    } finally {
      setDownloadingSize(null)
    }
  }

  const openIntent = (channel: ChannelKey, url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer')
    logShareEvent(channel)
    onClose()
  }

  const intentUrl = (channel: Exclude<ChannelKey, 'instagram'>) => {
    const encodedUrl = encodeURIComponent(shareUrl)
    const text = `Check out this recipe: ${recipeName}`
    const encodedText = encodeURIComponent(text)
    const ogImageUrl =
      typeof window !== 'undefined'
        ? `${window.location.origin}/api/og/recipe/${recipe.slug}${userSlug ? `?ref=${encodeURIComponent(userSlug)}` : ''}`
        : ''
    switch (channel) {
      case 'facebook':
        return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`
      case 'twitter':
        return `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`
      case 'linkedin':
        return `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`
      case 'pinterest': {
        // Pinterest will use the OG image automatically if we don't pass media,
        // but providing the OG card URL absolutizes it for the pin preview.
        const encodedMedia = encodeURIComponent(ogImageUrl)
        return `https://www.pinterest.com/pin/create/button/?url=${encodedUrl}&media=${encodedMedia}&description=${encodedText}`
      }
      case 'gmail':
      case 'outlook': {
        const emailSubject = encodeURIComponent(`Check out "${recipeName}" on Culineer`)
        const emailBody = encodeURIComponent(
          [text, '', shareUrl, '', ogImageUrl].filter(Boolean).join('\n')
        )
        if (channel === 'gmail')
          return `https://mail.google.com/mail/?view=cm&fs=1&su=${emailSubject}&body=${emailBody}`
        return `https://outlook.live.com/owa/?path=/mail/action/compose&subject=${emailSubject}&body=${emailBody}`
      }
      case 'sms':
        return `sms:?body=${encodedText}%20${encodedUrl}`
    }
  }

  const isGated = (channel: ChannelKey) => !hasPhoto && PHOTO_GATED.includes(channel)

  const allChannels: {
    key: ChannelKey
    label: string
    Icon: React.ElementType
    color: string
    touchOnly?: boolean
  }[] = [
    { key: 'instagram', label: 'Instagram', Icon: FaInstagram, color: '#E4405F' },
    { key: 'facebook', label: 'Facebook', Icon: FaFacebookF, color: '#1877F2' },
    { key: 'gmail', label: 'Gmail', Icon: SiGmail, color: '#EA4335' },
    { key: 'outlook', label: 'Outlook', Icon: SiMicrosoftoutlook, color: '#0078D4' },
    { key: 'pinterest', label: 'Pinterest', Icon: FaPinterestP, color: '#BD081C' },
    { key: 'twitter', label: 'X', Icon: FaXTwitter, color: '#000000' },
    { key: 'linkedin', label: 'LinkedIn', Icon: FaLinkedinIn, color: '#0A66C2' },
    { key: 'sms', label: 'Messages', Icon: MessageCircle, color: '#34C759', touchOnly: true },
  ]
  const channels = allChannels.filter((c) => !c.touchOnly || isTouchDevice)

  return (
    <>
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-center">Share this recipe</DialogTitle>
          <DialogDescription className="text-center">{recipeName}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Copy link */}
          <div>
            <h4 className="mb-2 text-sm text-grayText">Recipe link</h4>
            <div className="flex gap-2 rounded-lg border border-[#e5e7eb] p-2">
              <Input value={shareUrl} readOnly className="border-none" />
              <Button variant="ghost" size="icon" onClick={copyLink} aria-label="Copy link">
                <Copy className="h-4 w-4 text-[#6eac36]" />
              </Button>
            </div>
          </div>

          {/* Photo-gate banner */}
          {!hasPhoto && (
            <div className="flex items-start gap-3 rounded-lg border border-primary/30 bg-skyBg p-3">
              <ImageIcon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div className="flex-1 text-sm">
                <p className="font-medium">Add a photo to share publicly</p>
                <p className="text-grayText">
                  Recipes without a photo can still be sent by WhatsApp or email.
                </p>
                <Link
                  href={`/recipes/${recipe.slug}/edit`}
                  onClick={onClose}
                  className="mt-1 inline-block font-medium text-primary hover:underline"
                >
                  Add photo →
                </Link>
              </div>
            </div>
          )}

          {/* Channel buttons */}
          <div>
            <h4 className="mb-3 text-sm text-grayText">Share to</h4>
            <div className="grid grid-cols-4 gap-3 sm:grid-cols-7">
              {channels.map(({ key, label, Icon, color }) => {
                const gated = isGated(key)
                const busy = key === 'instagram' && igBusy
                const onClick = () => {
                  if (gated || busy) return
                  if (key === 'instagram') {
                    if (isTouchDevice) {
                      setIgPanelOpen((prev) => !prev)
                    } else {
                      shareToInstagram()
                    }
                  } else if (key === 'facebook' && isTouchDevice) {
                    navigator.share({ url: shareUrl, title: recipeName, text: `Check out this recipe: ${recipeName}` })
                      .then(() => { logShareEvent('facebook_web_share'); onClose() })
                      .catch((err) => { if ((err as DOMException)?.name !== 'AbortError') toast.error('Failed to share') })
                  } else {
                    openIntent(key, intentUrl(key))
                  }
                }
                const igSelected = key === 'instagram' && igPanelOpen
                return (
                  <button
                    key={key}
                    onClick={onClick}
                    disabled={gated || busy}
                    aria-label={`Share to ${label}`}
                    title={gated ? 'Add a photo to enable this' : label}
                    className={`flex items-center justify-center rounded-full border transition h-12 w-12 ${
                      gated
                        ? 'border-[#e5e7eb] cursor-not-allowed opacity-40'
                        : igSelected
                        ? 'border-[#E4405F] bg-[#E4405F]/10 hover:scale-105'
                        : 'border-[#e5e7eb] hover:scale-105 hover:shadow'
                    }`}
                  >
                    {busy ? (
                      <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    ) : (
                      <Icon className="h-5 w-5" style={{ color }} />
                    )}
                  </button>
                )
              })}
            </div>

            {igPanelOpen && isTouchDevice && (
              <div className="mt-3 rounded-xl border border-[#e5e7eb] bg-gray-50 p-3 space-y-3">
                <p className="text-xs text-center text-grayText font-medium">Your Instagram card</p>
                <div className="flex justify-center">
                  {igPreviewUrl ? (
                    <img
                      src={igPreviewUrl}
                      alt="Instagram card preview"
                      className="w-44 h-44 rounded-lg object-cover shadow-sm"
                    />
                  ) : (
                    <div className="w-44 h-44 rounded-lg bg-gray-100 flex items-center justify-center">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                  )}
                </div>
                <Button
                  onClick={() => shareToInstagram('square')}
                  disabled={igBusy || !igPreviewUrl}
                  className="w-full"
                >
                  {igBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Share to Instagram
                </Button>
                <button
                  onClick={() => shareToInstagram('story')}
                  disabled={igBusy || !igStoryCachedBlob}
                  className="w-full pb-1 text-sm text-center text-primary hover:underline disabled:opacity-50"
                >
                  {!igStoryCachedBlob ? 'Preparing story...' : 'Share as Story instead'}
                </button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>

    <Dialog open={igDownloadModalOpen} onOpenChange={setIgDownloadModalOpen}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-center">Save for Instagram</DialogTitle>
          <DialogDescription className="text-center">
            Instagram doesn't support posting from desktop browsers. Download your image and post it from the Instagram app on your phone.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 pt-2">
          <Button
            onClick={() => downloadIgImage('square')}
            disabled={downloadingSize !== null}
            className="w-full"
          >
            {downloadingSize === 'square' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Download Square (Feed Post)
          </Button>
          <Button
            variant="outline"
            onClick={() => downloadIgImage('story')}
            disabled={downloadingSize !== null}
            className="w-full"
          >
            {downloadingSize === 'story' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Download Story (1080×1920)
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  )
}
